/**
 * A trip simulated on a record — what the engine says on the day it returns,
 * set against today (owner, 22 Oct 2026 — one number before and after): one
 * trip from today and one from the return, the answer then in words, and only
 * the limits the trip moves, from what they counted to what they would ("In
 * total: 65 → 124 of 244 days") — or, for a title with no limit on absence,
 * its headline rule as it would stand. Whether the trip falls in the renewal
 * window or outlasts the title. No verdict of ours. Read alike by the
 * dashboard's planner and the public calculator's "what if I travel?".
 */
import { AlertTriangle } from '@tonnie/assets';
import {
  INOUT_CLOCK_STATUS_KEY,
  inoutAbsenceLimits,
  inoutAnswer,
  inoutAnswerFigure,
  inoutAnswerText,
  inoutClockHasNumber,
  inoutClockNameKey,
  inoutHeadlineView,
  inoutLimitMeterText,
  type InOutClock,
} from '@tonnie/heroes';
import { useCountryName, useLocale } from '@tonnie/locale';
import type { InOutReading, InOutTripReading } from '@tonnie/sdk';
import { cn, MetaList } from '@tonnie/ui';
import { INOUT_CLASSES } from '../lib/inout-tone';

/** The days a reading's answer gives — none when it has no number. */
const daysOf = (reading: InOutReading) =>
  inoutAnswerFigure(inoutAnswer(reading.evaluation))?.days ?? null;

export function InOutSimulationResult({
  answer,
  before,
}: {
  answer: InOutTripReading;
  /** The record as it reads today — the "before" the trip is set against. */
  before: InOutReading;
}) {
  const { t, formatDate, formatNumber } = useLocale();
  const countryName = useCountryName();
  const { evaluation } = answer;
  const { clock: headline } = inoutHeadlineView(evaluation);
  const limits = inoutAbsenceLimits(evaluation);
  const beforeById = new Map(before.evaluation.clocks.map((c) => [c.constraintId, c]));
  // The limits the trip moves: what each counted today, what it would on the return.
  const moved = limits.flatMap((clock) => {
    const then = beforeById.get(clock.constraintId);
    return inoutClockHasNumber(clock) &&
      then &&
      inoutClockHasNumber(then) &&
      then.value !== clock.value
      ? [{ clock, from: then.value }]
      : [];
  });
  const country = countryName(evaluation.country);
  const now = daysOf(before);
  const after = daysOf(answer);
  const afterText = inoutAnswerText(evaluation, {
    t,
    formatNumber,
    formatDate: (date) => formatDate(date, 'calendarDay'),
    country,
  });

  return (
    <div
      className="space-y-3 rounded-lg border border-border bg-muted/40 p-4 text-sm"
      data-testid="inout-simulation"
      role="status"
    >
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-md bg-card p-3">
          <p className="text-xs text-muted-foreground">{t('inout.planner.now')}</p>
          <p
            className="text-2xl font-bold tabular-nums text-foreground"
            data-testid="inout-simulation-now"
          >
            {now === null ? '—' : formatNumber(now)}
          </p>
        </div>
        <div className="rounded-md bg-card p-3">
          <p className="text-xs text-muted-foreground">
            {t('inout.planner.onReturn', {
              date: formatDate(evaluation.evaluatedOn, 'calendarDay'),
            })}
          </p>
          <p
            className={cn('text-2xl font-bold tabular-nums', INOUT_CLASSES.text)}
            data-testid="inout-simulation-after"
          >
            {after === null ? '—' : formatNumber(after)}
          </p>
        </div>
      </div>
      <p className="font-medium text-foreground" data-testid="inout-simulation-answer">
        {afterText.sentence}
      </p>
      {moved.map(({ clock, from }) => (
        <p
          key={clock.constraintId}
          className="flex flex-wrap items-center gap-x-2 text-muted-foreground tabular-nums"
          data-testid="inout-simulation-rule"
        >
          {t('inout.planner.limitMoved', {
            name: inoutLimitMeterText(clock, t, formatNumber).name,
            from: formatNumber(from),
            to: formatNumber(clock.value),
            limit: formatNumber(clock.limit),
          })}
          {clock.status !== 'WITHIN' && (
            <span className="font-medium text-tone-amber">
              {t(INOUT_CLOCK_STATUS_KEY[clock.status])}
            </span>
          )}
        </p>
      ))}
      {limits.length === 0 && headline && (
        <RuleLine clock={headline} country={country} testId="inout-simulation-rule" />
      )}
      {answer.duringRenewalWindow && (
        <p
          className="flex items-start gap-2 text-tone-amber"
          data-testid="inout-simulation-renewal"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('inout.planner.duringRenewalWindow')}
        </p>
      )}
      {answer.outlastsTitle && (
        <p className="flex items-start gap-2 text-tone-red" data-testid="inout-simulation-outlasts">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t('inout.planner.outlastsTitle')}
        </p>
      )}
    </div>
  );
}

/** A rule on the return: its name, what it counted, where it stands. */
function RuleLine({
  clock,
  country,
  testId,
}: {
  clock: InOutClock;
  country: string;
  testId: string;
}) {
  const { t, formatNumber } = useLocale();
  return (
    <MetaList className="text-muted-foreground" data-testid={testId}>
      <span>{t(inoutClockNameKey(clock), { country })}</span>
      <span>
        {inoutClockHasNumber(clock)
          ? t('inout.clock.counted', {
              value: formatNumber(clock.value),
              limit: formatNumber(clock.limit),
            })
          : t('inout.clock.noNumber')}
      </span>
      <span>{t(INOUT_CLOCK_STATUS_KEY[clock.status])}</span>
    </MetaList>
  );
}
